import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Accordion, Badge, Alert, Spinner, Form, Table } from "react-bootstrap";
import axios from "axios";
import { FaShoppingCart, FaUser, FaCalendarAlt, FaBox, FaPlus, FaTimes, FaEdit, FaTrash, FaSave, FaTimesCircle, FaMoneyBill } from "react-icons/fa";
import moment from "moment";
import Swal from 'sweetalert2';
import Select from "react-select";

export default function PetSaleComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [sales, setSales] = useState([]);
    const [appointmentNum, setAppointmentNum] = useState('');
    const [loading, setLoading] = useState(true);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [openNestedAccordion, setOpenNestedAccordion] = useState({});

    // Add Sale Form State
    const [showAddSaleModal, setShowAddSaleModal] = useState(false);

    // Edit Sale Form State
    const [showEditSaleModal, setShowEditSaleModal] = useState(false);
    const [editingSale, setEditingSale] = useState(null);

    // Delete Confirmation State
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingSale, setDeletingSale] = useState(null);

    // Payment Modal State
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);
    const [payments, setPayments] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);

    const [submitting, setSubmitting] = useState(false);
    const [products, setProducts] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [formData, setFormData] = useState({
        appointment_id: appointmentId,
        sale_date: moment().format('YYYY-MM-DD'),
        notes: '',
        products: []
    });
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        fetchSales();
    }, [appointmentId]);

    useEffect(() => {
        if (showAddSaleModal || showEditSaleModal) {
            fetchAvailableProducts();
        }
    }, [showAddSaleModal, showEditSaleModal]);

    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    // Edit and Delete permission only for Admin
    const canEditDelete = () => {
        return userRole === 'Admin';
    };

    // Check if sale is paid (locked for editing)
    const isSalePaid = (sale) => {
        return sale && sale.status === 'paid';
    };

    const fetchSales = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customer-sale/${appointmentId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data && response.data.status) {
                setSales(response.data.sales || []);
                setAppointmentNum(response.data.appointment_number || '');
                setOpenAccordion(String(appointmentId));
            } else {
                setSales([]);
            }
        } catch (error) {
            console.error("Error fetching sales:", error);
            setSales([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableProducts = async () => {
        try {
            setLoadingProducts(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/getAvailableProducts`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data && response.data.status) {
                setAvailableProducts(response.data.products || []);
                setPaymentMethods(response.data.paymentMethods || []);
            } else {
                setAvailableProducts([]);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
            setAvailableProducts([]);
        } finally {
            setLoadingProducts(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return moment(dateString).format("DD MMM YYYY hh:mm A");
    };

    const handleAddProduct = () => {
        setFormData({
            ...formData,
            products: [
                ...formData.products,
                {
                    product_id: '',
                    quantity: 1,
                    sale_price: 0,
                    discount_price: 0,
                    extra_price: 0,
                    temp_id: Date.now()
                }
            ]
        });
    };

    const handleRemoveProduct = (tempId) => {
        const productToRemove = formData.products.find(p => p.temp_id === tempId);
        if (productToRemove && productToRemove.product_id) {
            setSelectedProductIds(selectedProductIds.filter(id => id !== productToRemove.product_id));
        }
        setFormData({
            ...formData,
            products: formData.products.filter(p => p.temp_id !== tempId)
        });
    };

    const handleProductChange = (tempId, field, value) => {
        setFormData({
            ...formData,
            products: formData.products.map(product => {
                if (product.temp_id === tempId) {
                    const updatedProduct = { ...product, [field]: value === '' ? '' : value };

                    if (field === 'product_id' && value) {
                        const productId = parseInt(value);
                        if (selectedProductIds.includes(productId) && product.product_id !== productId) {
                            showAlert('warning', 'This product is already selected in another row');
                            return product;
                        }

                        if (product.product_id) {
                            setSelectedProductIds(selectedProductIds.filter(id => id !== product.product_id));
                        }

                        setSelectedProductIds([...selectedProductIds, productId]);

                        const selectedProduct = availableProducts.find(p => p.product_id === productId);
                        if (selectedProduct) {
                            updatedProduct.sale_price = selectedProduct.sale_price || 0;
                        }
                    }

                    return updatedProduct;
                }
                return product;
            })
        });
    };

    const handleNumberInputChange = (tempId, field, value) => {
        const numValue = value === '' ? '' : parseFloat(value);
        setFormData({
            ...formData,
            products: formData.products.map(product => {
                if (product.temp_id === tempId) {
                    return { ...product, [field]: numValue };
                }
                return product;
            })
        });
    };

    const handleFormFieldChange = (field, value) => {
        setFormData({
            ...formData,
            [field]: value
        });
        if (formErrors[field]) {
            setFormErrors({
                ...formErrors,
                [field]: null
            });
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.appointment_id) {
            errors.appointment_id = 'Appointment is required';
        }
        if (!formData.sale_date) {
            errors.sale_date = 'Sale date is required';
        }
        if (!formData.products || formData.products.length === 0) {
            errors.products = 'At least one product is required';
        } else {
            const productErrors = [];
            formData.products.forEach((product, index) => {
                if (!product.product_id) {
                    productErrors.push(`Product ${index + 1}: Product is required`);
                }
                if (!product.quantity || product.quantity < 1) {
                    productErrors.push(`Product ${index + 1}: Valid quantity is required`);
                }
                if (product.sale_price === undefined || product.sale_price === '' || product.sale_price < 0) {
                    productErrors.push(`Product ${index + 1}: Valid sale price is required`);
                }
                if (product.product_id && product.quantity) {
                    const selectedProduct = availableProducts.find(p => p.product_id === parseInt(product.product_id));
                    if (selectedProduct && product.quantity > selectedProduct.stock_qty) {
                        productErrors.push(`Product ${index + 1}: Only ${selectedProduct.stock_qty} items available in stock`);
                    }
                }
            });
            if (productErrors.length > 0) {
                errors.products = productErrors;
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddPayment = () => {
        const grandTotal = calculateGrandTotal();
        const currentPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        const pendingAmount = grandTotal - currentPaid;

        setPayments([
            ...payments,
            {
                id: null,
                payment_method_id: '',
                amount: pendingAmount > 0 ? pendingAmount : 0,
                payment_date: moment().format('YYYY-MM-DD'),
                temp_id: Date.now()
            }
        ]);
    };

    const handleRemovePayment = (tempId) => {
        const paymentToRemove = payments.find(p => p.temp_id === tempId);
        if (paymentToRemove && paymentToRemove.id) {
            // If payment has an ID, we'll mark it for deletion on the server
            setPayments(payments.filter(p => p.temp_id !== tempId));
        } else {
            setPayments(payments.filter(p => p.temp_id !== tempId));
        }
    };

    const handlePaymentChange = (tempId, field, value) => {
        setPayments(payments.map(payment => {
            if (payment.temp_id === tempId) {
                return { ...payment, [field]: value === '' ? '' : value };
            }
            return payment;
        }));
    };

    const validatePayments = (totalAmount) => {
        const totalPaid = payments.reduce((sum, payment) => {
            const amount = payment.amount === '' ? 0 : parseFloat(payment.amount);
            return sum + (amount || 0);
        }, 0);
        if (totalPaid > totalAmount) {
            setFormErrors({ ...formErrors, payments: `Total payments (${totalPaid}) cannot exceed sale total (${totalAmount})` });
            return false;
        }
        return true;
    };

    const showAlert = (type, message) => {
        Swal.fire({
            icon: type,
            title: type === 'success' ? 'Success!' : type === 'warning' ? 'Warning!' : 'Error!',
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true
        });
    };

    const handleSubmitSale = async () => {
        if (!validateForm()) {
            return;
        }

        const grandTotal = calculateGrandTotal();
        if (!validatePayments(grandTotal)) {
            return;
        }

        setSubmitting(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const submitData = {
                appointment_id: formData.appointment_id,
                sale_date: formData.sale_date,
                notes: formData.notes,
                products: formData.products.map(({ temp_id, ...product }) => ({
                    product_id: parseInt(product.product_id),
                    quantity: parseInt(product.quantity),
                    sale_price: parseFloat(product.sale_price || 0),
                    discount_price: parseFloat(product.discount_price || 0),
                    extra_price: parseFloat(product.extra_price || 0)
                })),
                payments: payments.map(({ temp_id, id, ...payment }) => ({
                    payment_method_id: parseInt(payment.payment_method_id),
                    amount: parseFloat(payment.amount || 0),
                    payment_date: payment.payment_date
                }))
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/customer-sale`,
                submitData,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data && response.data.status) {
                setShowAddSaleModal(false);
                resetForm();
                await fetchSales();
                showAlert('success', 'Sale created successfully');
            } else {
                setFormErrors({ submit: response.data.message || 'Failed to create sale' });
            }
        } catch (error) {
            console.error("Error creating sale:", error);
            if (error.response && error.response.data) {
                if (error.response.data.errors) {
                    const laravelErrors = error.response.data.errors;
                    const formattedErrors = {};

                    if (laravelErrors.appointment_id) {
                        formattedErrors.appointment_id = laravelErrors.appointment_id[0];
                    }
                    if (laravelErrors.sale_date) {
                        formattedErrors.sale_date = laravelErrors.sale_date[0];
                    }
                    if (laravelErrors.products) {
                        formattedErrors.products = laravelErrors.products;
                    }
                    if (laravelErrors.message) {
                        formattedErrors.submit = laravelErrors.message;
                    }

                    setFormErrors(formattedErrors);
                } else if (error.response.data.message) {
                    setFormErrors({ submit: error.response.data.message });
                } else {
                    setFormErrors({ submit: 'An error occurred while creating the sale' });
                }
            } else {
                setFormErrors({ submit: 'An error occurred while creating the sale' });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditClick = async (sale) => {
        if (isSalePaid(sale) && !canEditDelete()) {
            showAlert('warning', 'Cannot edit a paid sale. Only Admin can edit paid sales.');
            return;
        }

        if (!canEditDelete()) {
            showAlert('error', 'Only Admin can edit sales');
            return;
        }

        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customer-sale-edit/${sale.id}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data && response.data.status) {
                const saleData = response.data.sale;

                const saleProducts = (saleData.details || []).map((item, index) => ({
                    product_id: item.product_id,
                    quantity: item.quantity,
                    sale_price: parseFloat(item.sale_price) || 0,
                    discount_price: parseFloat(item.discount_price) || 0,
                    extra_price: parseFloat(item.extra_price) || 0,
                    temp_id: Date.now() + index
                }));

                const productIds = saleProducts.map(p => p.product_id);
                setSelectedProductIds(productIds);

                // Map existing payments
                const existingPayments = (sale.payments || []).map((payment, index) => ({
                    id: payment.id,
                    payment_method_id: payment.payment_method_id,
                    amount: parseFloat(payment.amount) || 0,
                    payment_date: moment(payment.payment_date).format('YYYY-MM-DD'),
                    temp_id: Date.now() + index + 1000
                }));

                setEditingSale(sale);
                setPayments(existingPayments);
                setFormData({
                    appointment_id: appointmentId,
                    sale_date: moment(sale.sale_date).format('YYYY-MM-DD'),
                    notes: sale.notes || '',
                    products: saleProducts
                });
                setShowEditSaleModal(true);
            } else {
                showAlert('error', 'Failed to load sale data');
            }
        } catch (error) {
            console.error("Error fetching sale for edit:", error);
            showAlert('error', error.response?.data?.message || 'An error occurred while loading sale data');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateSale = async () => {
        if (!validateForm()) {
            return;
        }

        const grandTotal = calculateGrandTotal();
        if (!validatePayments(grandTotal)) {
            return;
        }

        setSubmitting(true);
        try {
            const AuthToken = localStorage.getItem("authToken");

            // Separate payments to delete (those removed from UI)
            const currentPaymentIds = payments.filter(p => p.id).map(p => p.id);
            const originalPaymentIds = (editingSale.payments || []).map(p => p.id);
            const paymentsToDelete = originalPaymentIds.filter(id => !currentPaymentIds.includes(id));

            const submitData = {
                sale_date: formData.sale_date,
                notes: formData.notes,
                products: formData.products.map(({ temp_id, ...product }) => ({
                    product_id: parseInt(product.product_id),
                    quantity: parseInt(product.quantity),
                    sale_price: parseFloat(product.sale_price || 0),
                    discount_price: parseFloat(product.discount_price || 0),
                    extra_price: parseFloat(product.extra_price || 0)
                })),
                payments: payments.map(({ temp_id, ...payment }) => ({
                    id: payment.id || null,
                    payment_method_id: parseInt(payment.payment_method_id),
                    amount: parseFloat(payment.amount || 0),
                    payment_date: payment.payment_date
                })),
                delete_payments: paymentsToDelete
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/customer-sale-update/${editingSale.id}`,
                submitData,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data && response.data.status) {
                setShowEditSaleModal(false);
                resetForm();
                await fetchSales();
                showAlert('success', 'Sale updated successfully');
            } else {
                setFormErrors({ submit: response.data.message || 'Failed to update sale' });
            }
        } catch (error) {
            console.error("Error updating sale:", error);
            if (error.response && error.response.data) {
                if (error.response.data.errors) {
                    const laravelErrors = error.response.data.errors;
                    const formattedErrors = {};

                    if (laravelErrors.sale_date) {
                        formattedErrors.sale_date = laravelErrors.sale_date[0];
                    }
                    if (laravelErrors.products) {
                        formattedErrors.products = laravelErrors.products;
                    }
                    if (laravelErrors.message) {
                        formattedErrors.submit = laravelErrors.message;
                    }

                    setFormErrors(formattedErrors);
                } else if (error.response.data.message) {
                    setFormErrors({ submit: error.response.data.message });
                } else {
                    setFormErrors({ submit: 'An error occurred while updating the sale' });
                }
            } else {
                setFormErrors({ submit: 'An error occurred while updating the sale' });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleViewPayments = (sale) => {
        setSelectedSale(sale);
        setShowPaymentModal(true);
    };

    const handleDeleteClick = (sale) => {
        if (isSalePaid(sale) && !canEditDelete()) {
            showAlert('warning', 'Cannot delete a paid sale. Only Admin can delete paid sales.');
            return;
        }
        if (sale.payments && sale.payments.length > 0 && !canEditDelete()) {
            showAlert('warning', 'Cannot delete sale with existing payments');
            return;
        }
        setDeletingSale(sale);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        if (!deletingSale) return;

        setSubmitting(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/customer-sale-delete/${deletingSale.id}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data && response.data.status) {
                setShowDeleteConfirm(false);
                setDeletingSale(null);
                await fetchSales();
                showAlert('success', 'Sale deleted successfully');
            } else {
                showAlert('error', response.data.message || 'Failed to delete sale');
            }
        } catch (error) {
            console.error("Error deleting sale:", error);
            showAlert('error', error.response?.data?.message || 'An error occurred while deleting the sale');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            appointment_id: appointmentId,
            sale_date: moment().format('YYYY-MM-DD'),
            notes: '',
            products: []
        });
        setPayments([]);
        setFormErrors({});
        setEditingSale(null);
        setSelectedProductIds([]);
    };

    const calculateProductTotal = (product) => {
        const subtotal = ((product.sale_price || 0) * (product.quantity || 0));
        const discount = parseFloat(product.discount_price) || 0;
        const extra = parseFloat(product.extra_price) || 0;
        return (subtotal - discount + extra);
    };

    const calculateGrandTotal = () => {
        return formData.products.reduce((total, product) => {
            return total + parseFloat(calculateProductTotal(product));
        }, 0);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'paid':
                return <Badge bg="success">Paid</Badge>;
            case 'partial':
                return <Badge bg="warning">Partial</Badge>;
            case 'unpaid':
                return <Badge bg="danger">Unpaid</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    // Initialize with one empty product row when modal opens
    useEffect(() => {
        if (showAddSaleModal && formData.products.length === 0) {
            setFormData(prev => ({
                ...prev,
                products: [{
                    product_id: '',
                    quantity: 1,
                    sale_price: 0,
                    discount_price: 0,
                    extra_price: 0,
                    temp_id: Date.now()
                }]
            }));
        }
    }, [showAddSaleModal]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading sales') || 'Loading sales...'}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('sales list') || 'Sales List'}</h6>

                {isAppointmentOpen && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowAddSaleModal(true)}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaPlus size={14} />
                        {t('Add Sale') || 'Add Sale'}
                    </Button>
                )}
            </div>

            {!sales || sales.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaShoppingCart size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no sales found') || 'No sales found'}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="sales-accordion"
                >
                    <Accordion.Item eventKey={String(appointmentId)} className="mb-3 border rounded">
                        <Accordion.Header className="bg-light">
                            <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                    <div className="text-muted small">
                                        <strong className="text-primary">{appointmentNum}</strong>
                                    </div>
                                    <Badge bg="info" className="px-3 py-2">
                                        {sales.length} {t('sale(s)') || 'sale(s)'}
                                    </Badge>
                                </div>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body className="bg-white p-0">
                            <Accordion
                                activeKey={openNestedAccordion[appointmentId]}
                                onSelect={(key) => setOpenNestedAccordion({
                                    ...openNestedAccordion,
                                    [appointmentId]: key
                                })}
                                className="nested-sales-accordion"
                            >
                                {sales.map((sale) => (
                                    <Accordion.Item eventKey={String(sale.id)} key={sale.id} className="mb-3 border rounded">
                                        <Accordion.Header className="bg-light">
                                            <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                                <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                                    <FaShoppingCart className="text-primary" />
                                                    <Badge bg="primary" className="px-3 py-2">
                                                        {sale.sale_number || `#${sale.id}`}
                                                    </Badge>
                                                    <div className="d-flex align-items-center gap-3 text-muted small">
                                                        <span className="d-flex align-items-center gap-1">
                                                            <FaCalendarAlt size={14} />
                                                            {formatDate(sale.sale_date)}
                                                        </span>
                                                        <span className="d-flex align-items-center gap-1">
                                                            <FaUser size={14} />
                                                            {sale.customer_name}
                                                        </span>
                                                        <span className="d-flex align-items-center gap-1">
                                                            {getStatusBadge(sale.status)}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="info"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewPayments(sale);
                                                        }}
                                                        title="View Payments"
                                                    >
                                                        <FaMoneyBill />
                                                    </Button>
                                                    {canEditDelete() && (
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditClick(sale);
                                                            }}
                                                            title="Edit sale"
                                                        >
                                                            <FaEdit />
                                                        </Button>
                                                    )}
                                                    {canEditDelete() && (
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteClick(sale);
                                                            }}
                                                            title="Delete sale"
                                                        >
                                                            <FaTrash />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body className="bg-white">
                                            <Row className="g-4">
                                                <Col md={12}>
                                                    <div className="p-3 bg-light rounded">
                                                        <div className="mb-3">
                                                            <strong className="text-muted">{t('Sale Summary') || 'Sale Summary'}:</strong>
                                                        </div>

                                                        {sale.sale_summary && sale.sale_summary.length > 0 ? (
                                                            <div className="table-responsive mb-3">
                                                                <table className="table table-sm table-bordered">
                                                                    <thead className="table-light">
                                                                        <tr>
                                                                            <th>{t('Entered by') || 'Entered by'}</th>
                                                                            <th>{t('Product') || 'Product'}</th>
                                                                            <th className="text-center">{t('Quantity') || 'Quantity'}</th>
                                                                            <th className="text-end">{t('Total') || 'Total'}</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {sale.sale_summary.map((item, idx) => (
                                                                            <tr key={idx}>
                                                                                <td>{sale.entered_by}</td>
                                                                                <td>{item.name}</td>
                                                                                <td className="text-center">{item.qty}</td>
                                                                                <td className="text-end">{item.total_amount}</td>
                                                                            </tr>
                                                                        ))}
                                                                        <tr className="table-active">
                                                                            <td colSpan="3" className="text-end fw-bold">Total:</td>
                                                                            <td className="text-end fw-bold">{sale.total_amount}</td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <p className="text-muted mb-3">{t('No products found') || 'No products found'}</p>
                                                        )}

                                                        {sale.notes && (
                                                            <div className="mt-2">
                                                                <small className="text-muted">{t('Notes') || 'Notes'}: {typeof sale.notes === 'object' ? JSON.stringify(sale.notes) : sale.notes}</small>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            )}

            {/* Add Sale Modal */}
            <Modal
                show={showAddSaleModal}
                onHide={() => {
                    setShowAddSaleModal(false);
                    resetForm();
                }}
                size="xl"
                backdrop="static"
            >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>
                        <FaShoppingCart className="me-2" />
                        {t('Add New Sale') || 'Add New Sale'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formErrors.submit && (
                        <Alert variant="danger" onClose={() => setFormErrors({ ...formErrors, submit: null })} dismissible>
                            {formErrors.submit}
                        </Alert>
                    )}

                    <Form>
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>
                                        <FaCalendarAlt className="me-1" />
                                        {t('Sale Date') || 'Sale Date'} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.sale_date}
                                        onChange={(e) => handleFormFieldChange('sale_date', e.target.value)}
                                        isInvalid={!!formErrors.sale_date}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.sale_date}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">
                                    <FaBox className="me-1" />
                                    {t('Products') || 'Products'} <span className="text-danger">*</span>
                                </Form.Label>
                            </div>

                            {loadingProducts && (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                    <span className="ms-2">{t('Loading products...') || 'Loading products...'}</span>
                                </div>
                            )}

                            {formErrors.products && Array.isArray(formErrors.products) && (
                                <Alert variant="danger" className="py-2">
                                    <ul className="mb-0">
                                        {formErrors.products.map((error, idx) => (
                                            <li key={idx}>{error}</li>
                                        ))}
                                    </ul>
                                </Alert>
                            )}

                            {formData.products.length === 0 ? (
                                <Alert variant="info" className="text-center">
                                    {t('Click "Add Product" to add items to this sale') || 'Click "Add Product" to add items to this sale'}
                                </Alert>
                            ) : (
                                <div className="table-responsive">
                                    <Table bordered hover className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '30%' }}>{t('Product') || 'Product'} <span className="text-danger"></span></th>
                                                <th style={{ width: '10%' }}>{t('Quantity') || 'Quantity'} <span className="text-danger"></span></th>
                                                <th style={{ width: '12%' }}>{t('Sale Price') || 'Sale Price'} <span className="text-danger"></span></th>
                                                <th style={{ width: '12%' }}>{t('Discount') || 'Discount'}</th>
                                                <th style={{ width: '12%' }}>{t('Extra Price') || 'Extra Price'}</th>
                                                <th style={{ width: '12%' }}>{t('Subtotal') || 'Subtotal'}</th>
                                                <th style={{ width: '5%' }} className="text-center">{t('Action') || 'Action'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.products.map((product, index) => (
                                                <tr key={product.temp_id}>
                                                    <td>
                                                        <Select
                                                            options={availableProducts
                                                                .filter(prod => !selectedProductIds.includes(prod.product_id) || prod.product_id === product.product_id)
                                                                .map(prod => ({
                                                                    value: prod.product_id,
                                                                    label: `${prod.product_name} - ${prod.vendor_name} (${prod.stock_qty} in stock)`
                                                                }))}
                                                            value={availableProducts
                                                                .filter(prod => prod.product_id === product.product_id)
                                                                .map(prod => ({
                                                                    value: prod.product_id,
                                                                    label: `${prod.product_name} - ${prod.vendor_name} (${prod.stock_qty} in stock)`
                                                                }))[0] || null}
                                                            onChange={(selectedOption) => handleProductChange(product.temp_id, "product_id", selectedOption ? selectedOption.value : "")}
                                                            placeholder={t('Choose a product...') || 'Choose a product...'}
                                                            isClearable
                                                            classNamePrefix="react-select"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min="1"
                                                            placeholder="Qty"
                                                            value={product.quantity === '' ? '' : product.quantity}
                                                            onChange={(e) => handleNumberInputChange(product.temp_id, 'quantity', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            placeholder="Price"
                                                            value={product.sale_price === '' ? '' : product.sale_price}
                                                            onChange={(e) => handleNumberInputChange(product.temp_id, 'sale_price', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            placeholder="Discount"
                                                            value={product.discount_price === '' ? '' : product.discount_price}
                                                            onChange={(e) => handleNumberInputChange(product.temp_id, 'discount_price', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            placeholder="Extra"
                                                            value={product.extra_price === '' ? '' : product.extra_price}
                                                            onChange={(e) => handleNumberInputChange(product.temp_id, 'extra_price', e.target.value)}
                                                        />
                                                    </td>
                                                    <td className="align-middle">
                                                        <strong>{calculateProductTotal(product)}</strong>
                                                    </td>
                                                    <td className="text-center align-middle">
                                                        <Button
                                                            variant="link"
                                                            className="text-danger p-0"
                                                            onClick={() => handleRemoveProduct(product.temp_id)}
                                                            title="Remove product"
                                                        >
                                                            <FaTimes />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                            <div className="d-flex justify-content-between align-items-center mb-2 p-2">

                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleAddProduct}
                                    disabled={loadingProducts}
                                >
                                    <FaPlus className="me-1" size={12} />
                                    {t('Add Product') || 'Add Product'}
                                </Button>
                            </div>
                        </div>

                        {/* Payments Section */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">
                                    <FaMoneyBill className="me-1" />
                                    {t('Payments') || 'Payments'}
                                </Form.Label>
                            </div>

                            {formErrors.payments && (
                                <Alert variant="danger" className="py-2">
                                    {formErrors.payments}
                                </Alert>
                            )}

                            {payments.length === 0 ? (
                                <Alert variant="info" className="text-center py-2">
                                    No payments added yet. Click "Add Payment" to add payments.
                                </Alert>
                            ) : (
                                <div className="table-responsive">
                                    <Table bordered hover size="sm">
                                        <thead className="table-light">
                                            <tr>
                                                <th>{t('Payment Method') || 'Payment Method'}</th>
                                                <th>{t('Amount') || 'Amount'}</th>
                                                <th>{t('Payment Date') || 'Payment Date'}</th>
                                                <th style={{ width: '5%' }} className="text-center">{t('Action') || 'Action'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.map((payment, index) => (
                                                <tr key={payment.temp_id}>
                                                    <td>
                                                        <Form.Select
                                                            value={payment.payment_method_id}
                                                            onChange={(e) => handlePaymentChange(payment.temp_id, 'payment_method_id', e.target.value)}
                                                            size="sm"
                                                        >
                                                            <option value="">{t('Select method...') || 'Select method...'}</option>
                                                            {paymentMethods.map(method => (
                                                                <option key={method.id} value={method.id}>
                                                                    {method.name}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            placeholder="Amount"
                                                            value={payment.amount === '' ? '' : payment.amount}
                                                            onChange={(e) => handlePaymentChange(payment.temp_id, 'amount', e.target.value)}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="date"
                                                            value={payment.payment_date}
                                                            onChange={(e) => handlePaymentChange(payment.temp_id, 'payment_date', e.target.value)}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td className="text-center align-middle">
                                                        <Button
                                                            variant="link"
                                                            className="text-danger p-0"
                                                            onClick={() => handleRemovePayment(payment.temp_id)}
                                                            title="Remove payment"
                                                        >
                                                            <FaTimes />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={handleAddPayment}
                                >
                                    <FaPlus className="me-1" size={12} />
                                    {t('Add Payment') || 'Add Payment'}
                                </Button>
                            </div>
                        </div>

                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t('Notes') || 'Notes'}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={formData.notes}
                                        onChange={(e) => handleFormFieldChange('notes', e.target.value)}
                                        placeholder={t('Additional notes...') || 'Additional notes...'}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {formData.products.length > 0 && (
                            <div className="bg-light p-3 rounded">
                                <div className="d-flex justify-content-between align-items-center">
                                    <strong className="h5 mb-0">{t('Grand Total') || 'Grand Total'}:</strong>
                                    <strong className="h4 mb-0 text-primary">
                                        {calculateGrandTotal()}
                                    </strong>
                                </div>
                                {payments.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                        <span className="text-muted">Total Payments:</span>
                                        <strong>
                                            {payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)}
                                        </strong>
                                    </div>
                                )}
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer className="p-2">
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowAddSaleModal(false);
                            resetForm();
                        }}
                        disabled={submitting}
                    >
                        {t('Cancel') || 'Cancel'}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmitSale}
                        disabled={submitting || formData.products.length === 0}
                    >
                        {submitting ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                {t('Creating...') || 'Creating...'}
                            </>
                        ) : (
                            <>
                                <FaSave className="me-2" />
                                {t('Create Sale') || 'Create Sale'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Sale Modal */}
            <Modal
                show={showEditSaleModal}
                onHide={() => {
                    setShowEditSaleModal(false);
                    resetForm();
                }}
                size="xl"
                backdrop="static"
            >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>
                        <FaEdit className="me-2" />
                        {t('Edit Sale') || 'Edit Sale'} - {editingSale?.sale_number}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formErrors.submit && (
                        <Alert variant="danger" onClose={() => setFormErrors({ ...formErrors, submit: null })} dismissible>
                            {formErrors.submit}
                        </Alert>
                    )}

                    <Form>
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>
                                        <FaCalendarAlt className="me-1" />
                                        {t('Sale Date') || 'Sale Date'} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.sale_date}
                                        onChange={(e) => handleFormFieldChange('sale_date', e.target.value)}
                                        isInvalid={!!formErrors.sale_date}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.sale_date}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">
                                    <FaBox className="me-1" />
                                    {t('Products') || 'Products'} <span className="text-danger">*</span>
                                </Form.Label>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleAddProduct}
                                    disabled={loadingProducts}
                                >
                                    <FaPlus className="me-1" size={12} />
                                    {t('Add Product') || 'Add Product'}
                                </Button>
                            </div>

                            {loadingProducts && (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                    <span className="ms-2">{t('Loading products...') || 'Loading products...'}</span>
                                </div>
                            )}

                            {formData.products.length === 0 ? (
                                <Alert variant="info" className="text-center">
                                    {t('Click "Add Product" to add items to this sale') || 'Click "Add Product" to add items to this sale'}
                                </Alert>
                            ) : (
                                <div className="table-responsive">
                                    <Table bordered hover className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '30%' }}>{t('Product') || 'Product'} <span className="text-danger">*</span></th>
                                                <th style={{ width: '10%' }}>{t('Quantity') || 'Quantity'} <span className="text-danger">*</span></th>
                                                <th style={{ width: '12%' }}>{t('Sale Price') || 'Sale Price'} <span className="text-danger">*</span></th>
                                                <th style={{ width: '12%' }}>{t('Discount') || 'Discount'}</th>
                                                <th style={{ width: '12%' }}>{t('Extra Price') || 'Extra Price'}</th>
                                                <th style={{ width: '12%' }}>{t('Subtotal') || 'Subtotal'}</th>
                                                <th style={{ width: '5%' }} className="text-center">{t('Action') || 'Action'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.products.map((product, index) => (
                                                <tr key={product.temp_id}>
                                                    <td>
                                                        <Select
                                                            options={availableProducts
                                                                .filter(prod => !selectedProductIds.includes(prod.product_id) || prod.product_id === product.product_id)
                                                                .map(prod => ({
                                                                    value: prod.product_id,
                                                                    label: `${prod.product_name} - ${prod.vendor_name} (${prod.stock_qty} in stock)`
                                                                }))}
                                                            value={availableProducts
                                                                .filter(prod => prod.product_id === product.product_id)
                                                                .map(prod => ({
                                                                    value: prod.product_id,
                                                                    label: `${prod.product_name} - ${prod.vendor_name} (${prod.stock_qty} in stock)`
                                                                }))[0] || null}
                                                            onChange={(selectedOption) => handleProductChange(product.temp_id, "product_id", selectedOption ? selectedOption.value : "")}
                                                            placeholder={t('Choose a product...') || 'Choose a product...'}
                                                            isClearable
                                                            classNamePrefix="react-select"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min="1"
                                                            placeholder="Qty"
                                                            value={product.quantity === '' ? '' : product.quantity}
                                                            onChange={(e) => handleNumberInputChange(product.temp_id, 'quantity', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            placeholder="Price"
                                                            value={product.sale_price === '' ? '' : product.sale_price}
                                                            onChange={(e) => handleNumberInputChange(product.temp_id, 'sale_price', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            placeholder="Discount"
                                                            value={product.discount_price === '' ? '' : product.discount_price}
                                                            onChange={(e) => handleNumberInputChange(product.temp_id, 'discount_price', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            placeholder="Extra"
                                                            value={product.extra_price === '' ? '' : product.extra_price}
                                                            onChange={(e) => handleNumberInputChange(product.temp_id, 'extra_price', e.target.value)}
                                                        />
                                                    </td>
                                                    <td className="align-middle">
                                                        <strong>{calculateProductTotal(product)}</strong>
                                                    </td>
                                                    <td className="text-center align-middle">
                                                        <Button
                                                            variant="link"
                                                            className="text-danger p-0"
                                                            onClick={() => handleRemoveProduct(product.temp_id)}
                                                            title="Remove product"
                                                        >
                                                            <FaTimes />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </div>

                        {/* Payments Section for Edit */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">
                                    <FaMoneyBill className="me-1" />
                                    {t('Payments') || 'Payments'}
                                </Form.Label>
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={handleAddPayment}
                                >
                                    <FaPlus className="me-1" size={12} />
                                    {t('Add Payment') || 'Add Payment'}
                                </Button>
                            </div>

                            {formErrors.payments && (
                                <Alert variant="danger" className="py-2">
                                    {formErrors.payments}
                                </Alert>
                            )}

                            {payments.length === 0 ? (
                                <Alert variant="info" className="text-center py-2">
                                    No payments added yet. Click "Add Payment" to add payments.
                                </Alert>
                            ) : (
                                <div className="table-responsive">
                                    <Table bordered hover size="sm">
                                        <thead className="table-light">
                                            <tr>
                                                <th>{t('Payment Method') || 'Payment Method'}</th>
                                                <th>{t('Amount') || 'Amount'}</th>
                                                <th>{t('Payment Date') || 'Payment Date'}</th>
                                                <th style={{ width: '5%' }} className="text-center">{t('Action') || 'Action'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.map((payment, index) => (
                                                <tr key={payment.temp_id}>
                                                    <td>
                                                        <Form.Select
                                                            value={payment.payment_method_id}
                                                            onChange={(e) => handlePaymentChange(payment.temp_id, 'payment_method_id', e.target.value)}
                                                            size="sm"
                                                        >
                                                            <option value="">{t('Select method...') || 'Select method...'}</option>
                                                            {paymentMethods.map(method => (
                                                                <option key={method.id} value={method.id}>
                                                                    {method.name}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            placeholder="Amount"
                                                            value={payment.amount === '' ? '' : payment.amount}
                                                            onChange={(e) => handlePaymentChange(payment.temp_id, 'amount', e.target.value)}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="date"
                                                            value={payment.payment_date}
                                                            onChange={(e) => handlePaymentChange(payment.temp_id, 'payment_date', e.target.value)}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td className="text-center align-middle">
                                                        <Button
                                                            variant="link"
                                                            className="text-danger p-0"
                                                            onClick={() => handleRemovePayment(payment.temp_id)}
                                                            title="Remove payment"
                                                        >
                                                            <FaTimes />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </div>

                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t('Notes') || 'Notes'}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={formData.notes}
                                        onChange={(e) => handleFormFieldChange('notes', e.target.value)}
                                        placeholder={t('Additional notes...') || 'Additional notes...'}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {formData.products.length > 0 && (
                            <div className="bg-light p-3 rounded">
                                <div className="d-flex justify-content-between align-items-center">
                                    <strong className="h5 mb-0">{t('Grand Total') || 'Grand Total'}:</strong>
                                    <strong className="h4 mb-0 text-primary">
                                        {calculateGrandTotal()}
                                    </strong>
                                </div>
                                {payments.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                        <span className="text-muted">Total Payments:</span>
                                        <strong>
                                            {payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)}
                                        </strong>
                                    </div>
                                )}
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer className="p-2">
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowEditSaleModal(false);
                            resetForm();
                        }}
                        disabled={submitting}
                    >
                        <FaTimesCircle className="me-2" />
                        {t('Cancel') || 'Cancel'}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUpdateSale}
                        disabled={submitting || formData.products.length === 0}
                    >
                        {submitting ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                {t('Updating...') || 'Updating...'}
                            </>
                        ) : (
                            <>
                                <FaSave className="me-2" />
                                {t('Update Sale') || 'Update Sale'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Payment Details Modal */}
            <Modal
                show={showPaymentModal}
                onHide={() => {
                    setShowPaymentModal(false);
                    setSelectedSale(null);
                }}
                size="lg"
            >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>
                        <FaMoneyBill className="me-2" />
                        {t('Payment Details') || 'Payment Details'} - {selectedSale?.sale_number}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedSale && (
                        <>
                            <div className="mb-3">
                                <strong>Customer:</strong> {selectedSale.customer_name}<br />
                                <strong>Total Amount:</strong> {selectedSale.total_amount}<br />
                                <strong>Paid Amount:</strong> {selectedSale.paid_amount}<br />
                                <strong>Status:</strong> {getStatusBadge(selectedSale.status)}
                            </div>


                            {selectedSale.payments && selectedSale.payments.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Payment Method</th>
                                                <th>Amount</th>
                                                <th>Payment Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedSale.payments.map((payment, idx) => (
                                                <tr key={idx}>
                                                    <td>{payment.payment_method?.name || payment.payment_method_id}</td>
                                                    <td>{payment.amount}</td>
                                                    <td>{formatDate(payment.payment_date)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <Alert variant="info">No payments recorded for this sale.</Alert>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="p-2">
                    <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteConfirm}
                onHide={() => {
                    setShowDeleteConfirm(false);
                    setDeletingSale(null);
                }}
                centered
            >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>
                        <FaTrash className="me-2 text-danger" />
                        {t('Confirm Delete') || 'Confirm Delete'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {t('Are you sure you want to delete sale') || 'Are you sure you want to delete sale'}{" "}
                        <strong>{deletingSale?.sale_number}</strong>?
                    </p>
                    <p className="text-danger mb-0">
                        <small>{t('This action cannot be undone.') || 'This action cannot be undone.'}</small>
                    </p>
                </Modal.Body>
                <Modal.Footer className="p-2">
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeletingSale(null);
                        }}
                        disabled={submitting}
                    >
                        {t('Cancel') || 'Cancel'}
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirmDelete}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                {t('Deleting...') || 'Deleting...'}
                            </>
                        ) : (
                            <>
                                <FaTrash className="me-2" />
                                {t('Delete Sale') || 'Delete Sale'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}